import React, { useCallback, useMemo, useRef } from 'react';
import { Breakpoints, intl, useBreakpoint } from '../../lib';
import type { ClassNameProp, ValidIconName } from '..';
import {
  AccessibleClickableContainer,
  Badge,
  FocusTool,
  HBox,
  Icon,
  MenuTool,
  Tool,
  VBox
} from '..';
import { isEmptyString } from '../../util';
import type { infrastructurePortalUtil } from 'generated-ts-common';
import { docIdToClassName } from './Document';

type ReadStatus = infrastructurePortalUtil.saf.infrastructure.model.document.ReadStatus;
type PatientDocument = {
  /**
   * Title of the document
   */
  title: string;
  /**
   * Content of the document
   */
  content: string;
  /**
   * Availability status of the document. If the document is deprecated, additional className 'deprecated' will be added to the content
   */
  availabilityStatus?: string;
  /**
   * ID of the document
   */
  uniqueDocID: infrastructurePortalUtil.saf.infrastructure.model.common.ID;
};

export type PatientDocumentProps =
  | ({
      /**
       * Necessary data about the document to be displayed
       */
      document: PatientDocument & {
        readStatus: ReadStatus;
        icon: ValidIconName;
      };
      /**
       * Options for the document when it's selected
       */
      selectOptions?: {
        /**
         * Indicate that the document is selected, which adds classes "selected" to the document container
         */
        isSelected: boolean;
        /**
         * Using for focus tool, which is the selector to focus on when the document is selected
         * When focus tool is clicked, the document container will focus to the element with this selector
         * When empty, the focus tool will not be displayed
         */
        focusToClassName?: string;
      };
      /**
       * To indicate if the tool is expanded or not (only for mobile)
       */
      toolExpanded?: boolean;
      /**
       * Actions to be displayed in the document, actions are expected to be a list of JSX elements (i.e. Tools)
       */
      actions?: JSX.Element[];
      /**
       *  Flag to determine if the document ID should be created from uniqueDocID
       */
      shouldCreateId?: boolean;
      /**
       * Callback when the document is clicked
       * @param id - The unique document ID of the document
       */
      onOpenDocument?: (id: PatientDocument['uniqueDocID']) => void;
      /**
       * Callback when the drawer is opened
       * @param id - The unique document ID of the document
       */
      onOpenDrawer?: (id: PatientDocument['uniqueDocID']) => void;
    } & ClassNameProp)
  | ({
      /**
       * Necessary data about the document to be displayed
       */
      document: PatientDocument & {
        readStatus: ReadStatus;
        icon: ValidIconName;
      };
      /**
       * Action to be displayed in the document, action is expected to be a JSX element (i.e. Tool)
       */
      actions?: JSX.Element | JSX.Element[];
      /**
       *  Flag to determine if the document ID should be created from uniqueDocID
       */
      shouldCreateId?: boolean;
    } & ClassNameProp);

type MinimalPatientDocumentProps = Extract<
  PatientDocumentProps,
  { actions?: JSX.Element | JSX.Element[] }
>;

type FullPatientDocumentProps = Extract<
  PatientDocumentProps,
  {
    onOpenDocument?: (id: PatientDocument['uniqueDocID']) => void;
  }
>;

export function PatientDocument(
  props: MinimalPatientDocumentProps
): JSX.Element;

export function PatientDocument(props: FullPatientDocumentProps): JSX.Element;
export function PatientDocument(props: PatientDocumentProps): JSX.Element {
  if (
    'onOpenDocument' in props ||
    'onOpenDrawer' in props ||
    'toolExpanded' in props ||
    'selectOptions' in props
  ) {
    return <FullPatientDocument {...props} />;
  }

  return <MinimalPatientDocument {...props} />;
}

/**
 * Component to display a patient document
 * @constructor
 */
const FullPatientDocument = ({
  document,
  toolExpanded,
  selectOptions,
  onOpenDrawer,
  onOpenDocument,
  actions,
  shouldCreateId = false,
  className = '',
  ...props
}: FullPatientDocumentProps): JSX.Element => {
  const isMobile = useBreakpoint(Breakpoints.MOBILE);
  const containerRef = useRef<HTMLDivElement>(null);
  const getContainer = useCallback(() => containerRef?.current, [containerRef]);

  const documentClassName = useMemo(() => {
    return [
      'ehs-document-patient',
      className,
      selectOptions?.isSelected && 'selected'
    ]
      .filter(Boolean)
      .join(' ');
  }, [selectOptions?.isSelected, className]);

  const handleOpenDocument = useCallback(() => {
    onOpenDocument?.(document.uniqueDocID);
  }, [document.uniqueDocID, onOpenDocument]);

  const handleOpenDrawer = useCallback(() => {
    onOpenDrawer?.(document.uniqueDocID);
  }, [document.uniqueDocID, onOpenDrawer]);

  return (
    <HBox className={documentClassName} ref={containerRef} {...props}>
      <AccessibleClickableContainer
        onClick={handleOpenDocument}
        className="ehs-document-patient-content"
      >
        <MinimalPatientDocument
          document={document}
          shouldCreateId={shouldCreateId}
        />
      </AccessibleClickableContainer>
      {isMobile && onOpenDrawer ? (
        <Tool
          icon="more"
          onClick={handleOpenDrawer}
          tooltip={intl('view.button.show_additional_tools')}
          aria-expanded={toolExpanded}
        />
      ) : (
        <>
          {selectOptions &&
            selectOptions.isSelected &&
            !isEmptyString(selectOptions.focusToClassName) && (
              <FocusTool
                selector={selectOptions.focusToClassName}
                tooltip={intl('healthrecordportlet.view.focusto.document')}
              />
            )}
          {actions && actions.length > 0 && (
            <MenuTool icon="more" containerElement={getContainer}>
              {actions}
            </MenuTool>
          )}
        </>
      )}
      {document.readStatus !== 'DISPLAYED' && (
        <Badge
          className="new-document-badge"
          badgeType="info"
          aria-label={intl('dashboardportlet.mobile.documents.present.title')}
        />
      )}
    </HBox>
  );
};

/**
 * The content for document tile in patient view.
 *
 * @return the created {@link JSX.Element}
 * @constructor
 */
const MinimalPatientDocument = ({
  document,
  actions,
  className = '',
  shouldCreateId = false,
  ...props
}: MinimalPatientDocumentProps): JSX.Element => {
  const isDeprecated =
    !isEmptyString(document.availabilityStatus) &&
    document.availabilityStatus.toLowerCase() === 'deprecated';

  const docId = useMemo(() => {
    if (!shouldCreateId) {
      return undefined;
    }
    return docIdToClassName({
      extension: document.uniqueDocID.extension,
      oid: document.uniqueDocID.oid
    });
  }, [
    document.uniqueDocID.extension,
    document.uniqueDocID.oid,
    shouldCreateId
  ]);

  const containerRef = useRef<HTMLDivElement>(null);
  const getContainer = useCallback(() => containerRef?.current, [containerRef]);

  return (
    <HBox
      className={`${className} ehs-patient-document-content`}
      ref={containerRef}
      {...props}
    >
      <div className="ehs-document-patient-content-icon-area">
        <div className="download-button" id={docId} />
        <Icon iconName={document.icon} aria-label={undefined} />
      </div>
      <VBox
        className={`ehs-document-patient-content-data ${
          isDeprecated ? 'deprecated' : ''
        }`.trim()}
      >
        <div className="font-16 boldText ellipsis">{document.title}</div>
        <div className="font-14 grey-text ellipsis">{document.content}</div>
      </VBox>
      {actions && Array.isArray(actions) && (
        <MenuTool icon="more" containerElement={getContainer}>
          {actions}
        </MenuTool>
      )}
      {actions && !Array.isArray(actions) && (
        <div className="document-patient-content-tool">{actions}</div>
      )}
    </HBox>
  );
};
